import type { PlantacionDeInforme } from "@/lib/informes";
import { numeroEscrito } from "@/lib/numeroEscrito";

import {
  agruparPorEspecie,
  claveDeEspecie,
  totalDeLasFilas,
  type FormularioDePlantacion,
  type GrupoDeEspecie,
  type TotalDePlantaciones,
} from "./plantaciones";
import { formatearSuperficie } from "./superficies";

/* ---------------------------------------------------------------------------
   El cuadro de plantaciones: lo que se DERIVA de las filas del informe para
   dibujar la lista (filtros, conteos, alertas de grupo, resumen).

   Es lógica PURA y vive fuera del componente por la razón de siempre: la regla
   de "qué se ve bajo este filtro" o la cuenta de un contador son las que pueden
   estar mal sin que nada se vea roto, y dentro del árbol un test no distingue
   una cifra buena de una cercana. La agrupación por especie y las sumas en
   centésimas son las de `plantaciones.ts`: acá solo se reusan.
--------------------------------------------------------------------------- */

/** Los filtros de estado del cuadro. `todas` es "ninguno puesto". */
export type FiltroDelCuadro = "todas" | "sin_validar" | "sin_fotos" | "incompletas";

export type FiltroDeEstado = Exclude<FiltroDelCuadro, "todas">;

/**
 * Una fila INCOMPLETA es la que no tiene plantas totales: le falta la
 * superficie o la densidad. Se mira lo que calculó el servidor y no los dos
 * campos por separado, para que la lista y el pie del cuadro (que suma esa
 * cifra) hablen de la misma fila.
 */
export function filaIncompleta(fila: PlantacionDeInforme): boolean {
  return fila.plantas_totales === null;
}

/**
 * Las plantas totales que el servidor va a calcular con lo escrito: superficie
 * × densidad, redondeada al entero más cercano.
 *
 * ES EL GEMELO DE `InformePlantacion::plantasTotales()` Y NO UNA CUENTA
 * PROPIA: la vista previa del formulario tiene que mostrar la cifra que va a
 * quedar guardada, con el mismo redondeo. Las dos direcciones del redondeo
 * (4.777,3 → 4.777 y 18.403,68 → 18.404) están medidas contra el cuadro del
 * prototipo. `null` —y nunca `0`— si falta la superficie o la densidad.
 *
 * La densidad pasa por `numeroEscrito` como al guardar: "1.111" son mil ciento
 * once, y leída como decimal daría una planta por hectárea.
 */
export function plantasTotalesDelFormulario(formulario: FormularioDePlantacion): number | null {
  const superficie = numeroEscrito(formulario.superficie_ha);
  const densidad = numeroEscrito(formulario.plantas_por_ha);

  if (superficie === null || densidad === null) return null;

  return Math.round(superficie * densidad);
}

/**
 * La alerta ámbar de la fila de grupo: cuántas filas del grupo no tienen
 * plantas totales y POR QUÉ, cuando el motivo es uno solo.
 *
 * Si todas las que faltan carecen de densidad dice "sin densidad"; si todas
 * carecen de superficie, "sin superficie"; si el motivo es mixto, "sin plantas
 * totales". Nombrar la causa evita que quien lo lee tenga que abrir cada fila
 * para saber qué completar. `null` cuando no hay nada que avisar.
 */
export function alertaDelGrupo(filas: PlantacionDeInforme[]): string | null {
  const faltantes = filas.filter(filaIncompleta);

  if (faltantes.length === 0) return null;

  const sinSuperficie = faltantes.filter((f) => f.superficie_ha === null).length;
  const sinDensidad = faltantes.filter((f) => f.plantas_por_ha === null).length;

  const causa =
    sinSuperficie === faltantes.length
      ? "superficie"
      : sinDensidad === faltantes.length
        ? "densidad"
        : "plantas totales";

  return `${faltantes.length} sin ${causa}`;
}

export interface GrupoDeLaVista extends Omit<GrupoDeEspecie, "filas"> {
  /** Solo las filas que pasan la búsqueda y el filtro. */
  filas: PlantacionDeInforme[];
  /** Cuántas filas tiene el grupo EN TOTAL, visibles o no. */
  cantidad: number;
  validadas: number;
  alerta: string | null;
  /** Alguna fila del grupo tiene superficie: sin ninguna, el pie dice "—" y no `0`. */
  conSuperficie: boolean;
}

export interface ResumenDelCuadro {
  cantidad: number;
  especies: number;
  superficieHa: number;
  plantas: number | null;
  validadas: number;
  /** Alguna fila tiene superficie: distingue "0 ha" de "ninguna cargada". */
  conSuperficie: boolean;
}

export interface VistaDelCuadro {
  grupos: GrupoDeLaVista[];
  /** Cuántas filas se ven con la búsqueda y el filtro puestos. */
  visibles: number;
  /** Cuántas filas cumplen cada filtro, sobre TODAS las filas. */
  conteos: Record<FiltroDeEstado, number>;
  resumen: ResumenDelCuadro;
  /** El pie del cuadro entero. */
  total: TotalDePlantaciones;
}

function cumple(fila: PlantacionDeInforme, filtro: FiltroDelCuadro): boolean {
  switch (filtro) {
    case "sin_validar":
      return !fila.validada;
    case "sin_fotos":
      return (fila.fotos ?? []).length === 0;
    case "incompletas":
      return filaIncompleta(fila);
    default:
      return true;
  }
}

/**
 * Lo que la lista dibuja: los grupos con sus filas visibles, los conteos de
 * los filtros y el resumen.
 *
 * LOS CONTEOS Y LOS PIES NO DEPENDEN DEL FILTRO: un chip dice cuántas filas
 * cumplen, no cuántas quedarían; y el pie de un grupo o del cuadro describe el
 * informe, no la búsqueda. Un total que cambia al teclear en el buscador se
 * leería como un dato del predio. El filtro y la búsqueda solo esconden filas,
 * y con ellas los grupos que se quedan sin ninguna.
 *
 * La búsqueda mira variedad, cuartel y especie, sin mayúsculas ni tildes (la
 * misma normalización que agrupa).
 */
export function vistaDelCuadro(
  filas: PlantacionDeInforme[],
  { busqueda, filtro }: { busqueda: string; filtro: FiltroDelCuadro },
): VistaDelCuadro {
  const buscada = claveDeEspecie(busqueda);

  const coincide = (fila: PlantacionDeInforme) =>
    cumple(fila, filtro) &&
    (buscada === "" ||
      [fila.variedad ?? "", fila.cuartel ?? "", fila.especie].some((texto) =>
        claveDeEspecie(texto).includes(buscada),
      ));

  const grupos = agruparPorEspecie(filas)
    .map<GrupoDeLaVista>((grupo) => ({
      ...grupo,
      filas: grupo.filas.filter(coincide),
      cantidad: grupo.filas.length,
      validadas: grupo.filas.filter((f) => f.validada).length,
      alerta: alertaDelGrupo(grupo.filas),
      conSuperficie: grupo.filas.some((f) => f.superficie_ha !== null),
    }))
    .filter((grupo) => grupo.filas.length > 0);

  const total = totalDeLasFilas(filas);

  return {
    grupos,
    visibles: grupos.reduce((n, g) => n + g.filas.length, 0),
    conteos: {
      sin_validar: filas.filter((f) => cumple(f, "sin_validar")).length,
      sin_fotos: filas.filter((f) => cumple(f, "sin_fotos")).length,
      incompletas: filas.filter((f) => cumple(f, "incompletas")).length,
    },
    resumen: {
      cantidad: filas.length,
      especies: agruparPorEspecie(filas).length,
      superficieHa: total.superficieHa,
      plantas: total.plantas,
      validadas: filas.filter((f) => f.validada).length,
      conSuperficie: filas.some((f) => f.superficie_ha !== null),
    },
    total,
  };
}

/**
 * El aviso de que lo plantado supera la superficie declarada del predio, o
 * `null` si no corresponde.
 *
 * ES UN AVISO Y NUNCA UNA RESTRICCIÓN (RF-E1 §8.2, la misma regla que el
 * cuadro de suelos): lo declarado puede estar mal y lo plantado bien, y
 * bloquear obligaría a falsear uno de los dos para poder guardar.
 *
 * `predioHa` llega YA EN HECTÁREAS: la unidad es del informe y la convierte
 * quien la conoce (`superficieEnHectareas`). Se compara en CENTÉSIMAS, como
 * `sumarSuperficies`, para que un desvío de punto flotante no aparezca como
 * un exceso. Sin superficie declarada o sin ninguna plantada no hay nada que
 * comparar.
 */
export function excesoSobreElPredio(resumen: ResumenDelCuadro, predioHa: number | null): string | null {
  if (predioHa === null || !resumen.conSuperficie) return null;

  const plantadas = Math.round(resumen.superficieHa * 100);
  const declaradas = Math.round(predioHa * 100);

  if (plantadas <= declaradas) return null;

  return (
    `La superficie plantada (${formatearSuperficie(plantadas / 100)} ha) supera la superficie `
    + `declarada del predio (${formatearSuperficie(declaradas / 100)} ha) en `
    + `${formatearSuperficie((plantadas - declaradas) / 100)} ha.`
  );
}

/** "27,19 ha plantadas · 2 especies": la bajada del título de la lista. */
export function textoDelResumen(resumen: ResumenDelCuadro): string {
  if (resumen.cantidad === 0) return "Agrega la primera plantación.";

  const especies = `${resumen.especies} ${resumen.especies === 1 ? "especie" : "especies"}`;

  return resumen.conSuperficie
    ? `${formatearSuperficie(resumen.superficieHa)} ha plantadas · ${especies}`
    : especies;
}
